<script setup>
/**
 * The first draft, line by line (spec P-8.1): every drafted line with where it came from, and Keep,
 * Edit or Remove beside it; the answers the same way; the questions only she can answer, each with
 * a box she can fill or leave empty to skip.
 *
 * It decides nothing and sends nothing: it reports her choices up (`update:decisions`,
 * `update:answered`), and the page turns them into `{ keep, edits }` (`helpers/setup-ai.js`).
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from './WasloInput.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import { LINE_DECISIONS, sourceKey } from '../helpers/setup-ai.js';

const props = defineProps({
  /** The BFF's `FirstDraftDTO`. */
  draft: { type: Object, required: true },
  /** `{ [id]: { decision, text, question?, answer? } }`. */
  decisions: { type: Object, required: true },
  /** `{ [questionId]: string }`. */
  answered: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:decisions', 'update:answered']);

const { t } = useWasloI18n();

const SECTION_LABEL = {
  identity: () => t('WASLO.PERSONALITY.FIELDS.IDENTITY.LABEL'),
  business: () => t('WASLO.PERSONALITY.FIELDS.BUSINESS.LABEL'),
  always: () => t('WASLO.PERSONALITY.FIELDS.ALWAYS.LABEL'),
  never: () => t('WASLO.PERSONALITY.FIELDS.NEVER.LABEL'),
};

const DECISION_LABEL = {
  keep: () => t('WASLO.SETUP_AI.REVIEW.KEEP'),
  edit: () => t('WASLO.SETUP_AI.REVIEW.EDIT'),
  remove: () => t('WASLO.SETUP_AI.REVIEW.REMOVE'),
};

const sections = computed(() =>
  ['identity', 'business', 'always', 'never']
    .map(section => ({
      section,
      label: SECTION_LABEL[section](),
      lines: props.draft.lines.filter(line => line.section === section),
    }))
    .filter(group => group.lines.length > 0)
);

const decisionOf = id =>
  props.decisions[id] ?? { decision: 'keep', text: null };

const decide = (id, decision) =>
  emit('update:decisions', {
    ...props.decisions,
    [id]: { ...decisionOf(id), decision },
  });

const editLine = (id, text) =>
  emit('update:decisions', {
    ...props.decisions,
    [id]: { ...decisionOf(id), decision: 'edit', text },
  });

const editAnswer = (id, field, value) =>
  emit('update:decisions', {
    ...props.decisions,
    [id]: { ...decisionOf(id), decision: 'edit', [field]: value },
  });

const answer = (id, value) =>
  emit('update:answered', { ...props.answered, [id]: value });

const sourceText = source => t(sourceKey(source));
</script>

<template>
  <div class="flex flex-col gap-8" data-waslo-first-draft-review>
    <section
      v-for="group in sections"
      :key="group.section"
      class="flex flex-col gap-3"
      :data-waslo-first-draft-section="group.section"
    >
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ group.label }}
      </h3>
      <div
        v-for="line in group.lines"
        :key="line.id"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
        :class="decisionOf(line.id).decision === 'remove' ? 'opacity-60' : ''"
        :data-waslo-first-draft-line="line.id"
      >
        <WasloTextArea
          v-if="decisionOf(line.id).decision === 'edit'"
          :model-value="decisionOf(line.id).text ?? line.text"
          :max-length="300"
          :show-count="false"
          min-height="2.5rem"
          :disabled="disabled"
          data-waslo-first-draft-edit
          @update:model-value="value => editLine(line.id, value)"
        />
        <p v-else class="mb-0 text-sm text-n-slate-12">
          <bdi>{{ line.text }}</bdi>
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-xs text-n-slate-11" data-waslo-first-draft-source>
            {{ sourceText(line.source) }}
          </span>
          <span class="flex-1" />
          <Button
            v-for="decision in LINE_DECISIONS"
            :key="decision"
            :label="DECISION_LABEL[decision]()"
            size="xs"
            :variant="
              decisionOf(line.id).decision === decision ? 'solid' : 'faded'
            "
            :color="
              decisionOf(line.id).decision === decision ? 'blue' : 'slate'
            "
            :disabled="disabled"
            :data-waslo-first-draft-decide="decision"
            @click="decide(line.id, decision)"
          />
        </div>
      </div>
    </section>

    <section
      v-if="draft.answers.length"
      class="flex flex-col gap-3"
      data-waslo-first-draft-answers
    >
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.SETUP_AI.REVIEW.ANSWERS') }}
      </h3>
      <div
        v-for="item in draft.answers"
        :key="item.id"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
        :class="decisionOf(item.id).decision === 'remove' ? 'opacity-60' : ''"
        :data-waslo-first-draft-answer="item.id"
      >
        <template v-if="decisionOf(item.id).decision === 'edit'">
          <WasloInput
            :model-value="decisionOf(item.id).question ?? item.question"
            :label="t('WASLO.SETUP_AI.REVIEW.QUESTION')"
            :disabled="disabled"
            @update:model-value="
              value => editAnswer(item.id, 'question', value)
            "
          />
          <WasloTextArea
            :model-value="decisionOf(item.id).answer ?? item.answer"
            :label="t('WASLO.SETUP_AI.REVIEW.ANSWER')"
            :max-length="2000"
            :show-count="false"
            :disabled="disabled"
            @update:model-value="value => editAnswer(item.id, 'answer', value)"
          />
        </template>
        <template v-else>
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            <bdi>{{ item.question }}</bdi>
          </p>
          <p class="mb-0 text-sm text-n-slate-12">
            <bdi>{{ item.answer }}</bdi>
          </p>
        </template>
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-xs text-n-slate-11">
            {{ sourceText(item.source) }}
          </span>
          <span class="flex-1" />
          <Button
            v-for="decision in LINE_DECISIONS"
            :key="decision"
            :label="DECISION_LABEL[decision]()"
            size="xs"
            :variant="
              decisionOf(item.id).decision === decision ? 'solid' : 'faded'
            "
            :color="
              decisionOf(item.id).decision === decision ? 'blue' : 'slate'
            "
            :disabled="disabled"
            :data-waslo-first-draft-decide="decision"
            @click="decide(item.id, decision)"
          />
        </div>
      </div>
    </section>

    <section
      v-if="draft.questions.length"
      class="flex flex-col gap-3"
      data-waslo-first-draft-questions
    >
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.SETUP_AI.REVIEW.QUESTIONS') }}
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.SETUP_AI.REVIEW.QUESTIONS_HINT') }}
      </p>
      <WasloInput
        v-for="question in draft.questions"
        :key="question.id"
        :model-value="answered[question.id] ?? ''"
        :label="question.ask"
        :placeholder="t('WASLO.SETUP_AI.REVIEW.SKIP_PLACEHOLDER')"
        :disabled="disabled"
        :data-waslo-first-draft-question="question.id"
        @update:model-value="value => answer(question.id, value)"
      />
    </section>
  </div>
</template>
